import { useEffect } from 'react';
import { isTouchViewport } from '@/hooks/use-mobile';
import { isKeyboardOpen } from './keyboard';

const isTextField = (el: Element | null): el is HTMLElement =>
	!!el && (el.matches('input, textarea, select') || (el as HTMLElement).isContentEditable);

const px = (value: string) => parseFloat(value) || 0;

// Room left around a revealed field, unless its scroll-margin asks for more.
const REVEAL_GAP = 12;

/** The top edge of a bar pinned to the bottom (a Save bar) that is drawn over `field`. */
function coveringBarTop(field: HTMLElement) {
	const rect = field.getBoundingClientRect();
	const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.bottom - 2);
	if (!hit || field.contains(hit)) return null;
	for (let el: Element | null = hit; el && el !== document.body; el = el.parentElement) {
		if (el.contains(field)) return null;
		const style = getComputedStyle(el);
		if (style.position === 'sticky' || style.position === 'fixed') {
			const bottomBar = style.top === 'auto' && style.bottom !== 'auto';
			return bottomBar ? el.getBoundingClientRect().top : null;
		}
	}
	return null;
}

/**
 * The scroll areas (`overflow: auto | scroll`) around `el`, innermost first,
 * each with the band of it that is visible: above the keyboard and inside
 * its scroll-padding.
 */
function* scrollAreas(el: HTMLElement, vv: VisualViewport) {
	for (let box = el.parentElement; box && box !== document.body; box = box.parentElement) {
		const style = getComputedStyle(box);
		if (!/(auto|scroll|overlay)/.test(style.overflowY) || box.scrollHeight <= box.clientHeight) continue;
		const frame = box.getBoundingClientRect();
		const inner = frame.top + box.clientTop;
		// A scroll area may reach under the keyboard; only its part above it counts.
		const top = Math.max(inner, vv.offsetTop) + px(style.scrollPaddingTop);
		const bottom = Math.min(inner + box.clientHeight, vv.offsetTop + vv.height) - px(style.scrollPaddingBottom);
		yield { box, top, bottom };
	}
}

/**
 * Scrolls `field` into the visible part of the scroll areas around it (above
 * the keyboard and any sticky Save bar, inside their scroll-padding, with its
 * own scroll-margin or a small gap), much as `scrollIntoView({ block:
 * 'nearest' })` would. Unlike scrollIntoView it only moves `overflow: auto |
 * scroll` areas, never the window or an `overflow: hidden` box the person
 * couldn't scroll back.
 */
function revealField(field: HTMLElement, vv: VisualViewport) {
	const margin = getComputedStyle(field);
	for (const { box, top, bottom } of scrollAreas(field, vv)) {
		const rect = field.getBoundingClientRect();
		const want = {
			top: rect.top - Math.max(REVEAL_GAP, px(margin.scrollMarginTop)),
			bottom: rect.bottom + Math.max(REVEAL_GAP, px(margin.scrollMarginBottom)),
		};
		let delta = want.bottom > bottom ? want.bottom - bottom : 0;
		// Too tall to fit: keep its top in view.
		if (want.top - delta < top) delta = want.top - top;
		if (Math.abs(delta) >= 1) box.scrollTop += delta;
		// A bar pinned over the scroll area without scroll-padding for it
		// (landscape phones, tablets): go above it, keeping the field's top in view.
		const barTop = coveringBarTop(field);
		if (barTop !== null) {
			const now = field.getBoundingClientRect();
			const extra = Math.min(now.bottom + REVEAL_GAP - barTop, now.top - REVEAL_GAP - top);
			if (extra >= 1) box.scrollTop += extra;
		}
	}
}

/**
 * Scrolls down just enough to show the rest of `group` (the field's parent)
 * when something appears under the field while typing, such as a note's Save
 * button or an error message. Only when the whole group fits above the
 * keyboard, and never upwards, so it can't pull the caret out of view.
 */
function revealGroup(group: HTMLElement, vv: VisualViewport) {
	for (const { box, top, bottom } of scrollAreas(group, vv)) {
		const rect = group.getBoundingClientRect();
		if (rect.height + 2 * REVEAL_GAP > bottom - top) return;
		const delta = rect.bottom + REVEAL_GAP - bottom;
		if (delta >= 1) box.scrollTop += delta;
	}
}

/**
 * Keeps `--app-height` equal to the visible height on touch screens (phones
 * in either orientation, tablets). Desktop keeps the 100dvh default.
 *
 * iOS Safari slides the keyboard over the page without resizing it, so a
 * 100dvh shell leaves the composer under the keyboard. Following the visual
 * viewport shrinks the shell above the keyboard instead, the way a native app
 * behaves, and the field being typed in is scrolled into view inside its page.
 */
export function MobileViewportSync() {
	useEffect(() => {
		const vv = window.visualViewport;
		if (!vv) return;
		const root = document.documentElement;
		let frame = 0;
		let revealFrame = 0;
		let appHeight = 0;

		// After the shell has laid out at its new height (and the tab bar has hidden).
		const revealSoon = () => {
			cancelAnimationFrame(revealFrame);
			revealFrame = requestAnimationFrame(() => {
				const field = document.activeElement;
				if (isTextField(field) && isKeyboardOpen()) revealField(field, vv);
			});
		};

		const update = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				const keyboard = isKeyboardOpen();
				// `kb-open:` styles key off this attribute.
				root.toggleAttribute('data-keyboard-open', keyboard);
				if (!isTouchViewport()) {
					root.style.removeProperty('--app-height');
					root.style.removeProperty('--keyboard-inset');
					appHeight = 0;
					return;
				}
				// Pinch zoom also shrinks the visual viewport; only follow it at 1x.
				if (Math.abs(vv.scale - 1) > 0.01) return;
				const height = Math.round(vv.height);
				root.style.setProperty('--app-height', `${height}px`);
				// Fixed, bottom-anchored sheets sit this far up to clear the keyboard.
				const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
				root.style.setProperty('--keyboard-inset', `${Math.round(inset)}px`);
				const typing = isTextField(document.activeElement);
				let moved = height !== appHeight;
				appHeight = height;
				// iOS scrolls the page up to reveal a focused field. The shell
				// already fits above the keyboard, so put the page back...
				if (window.scrollY !== 0 && typing) {
					window.scrollTo(0, 0);
					moved = true;
				}
				// ...and reveal the field inside its own scroll area instead.
				if (typing && keyboard && moved) revealSoon();
			});
		};

		// Watches the focused field's parent for what appears under it while
		// typing (a Save button that shows once there is text).
		let groupHeight = 0;
		const grew = new ResizeObserver(([entry]) => {
			const group = entry.target as HTMLElement;
			const height = group.offsetHeight;
			const taller = height > groupHeight;
			groupHeight = height;
			if (taller && isKeyboardOpen() && document.activeElement?.parentElement === group) revealGroup(group, vv);
		});

		const onFocusIn = (event: FocusEvent) => {
			if (!(event.target instanceof Element) || !isTextField(event.target)) return;
			// Moving to another field with the keyboard already up (iOS's ⌄ arrow, Next).
			if (isKeyboardOpen()) revealSoon();
			const group = event.target.parentElement;
			grew.disconnect();
			if (group && isTouchViewport()) {
				groupHeight = group.offsetHeight;
				grew.observe(group);
			}
		};
		const onFocusOut = () => grew.disconnect();

		update();
		vv.addEventListener('resize', update);
		vv.addEventListener('scroll', update);
		document.addEventListener('focusin', onFocusIn);
		document.addEventListener('focusout', onFocusOut);
		return () => {
			cancelAnimationFrame(frame);
			cancelAnimationFrame(revealFrame);
			grew.disconnect();
			vv.removeEventListener('resize', update);
			vv.removeEventListener('scroll', update);
			document.removeEventListener('focusin', onFocusIn);
			document.removeEventListener('focusout', onFocusOut);
			root.style.removeProperty('--app-height');
			root.style.removeProperty('--keyboard-inset');
			root.removeAttribute('data-keyboard-open');
		};
	}, []);

	return null;
}
